import React, { useState, useEffect, useCallback } from 'react';
import { Globe, ExternalLink, RefreshCw, Check, Copy, Shield, Database, Key, Settings2, LayoutDashboard, Zap, ChevronRight, Terminal } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { toast } from '../hooks/use-toast';
import api from '../utils/api';

const STATUS_MAP = {
  ready: { label: 'Live', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', dot: 'bg-emerald-400' },
  building: { label: 'Building', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30', dot: 'bg-amber-400 animate-pulse' },
  queued: { label: 'Queued', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', dot: 'bg-blue-400 animate-pulse' },
  error: { label: 'Error', color: 'bg-red-500/20 text-red-400 border-red-500/30', dot: 'bg-red-400' },
};

const TABS = [
  { id: 'overview', label: 'Overview', icon: LayoutDashboard },
  { id: 'domain', label: 'Domain', icon: Globe },
  { id: 'resources', label: 'Resources', icon: Settings2 },
  { id: 'database', label: 'Database', icon: Database },
  { id: 'secrets', label: 'Secrets', icon: Key },
];

const inputClass = "bg-background border-input text-foreground placeholder:text-muted-foreground focus:border-blue-500/50";

const DeployPanel = ({ project }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [deployment, setDeployment] = useState(null);
  const [deploying, setDeploying] = useState(false);
  const [isAnnual, setIsAnnual] = useState(false);
  const [tokens, setTokens] = useState({ has_vercel: false, has_railway: false });
  const [secrets, setSecrets] = useState([]);
  const [newSecretKey, setNewSecretKey] = useState('');
  const [newSecretVal, setNewSecretVal] = useState('');
  const [customDomain, setCustomDomain] = useState('');
  const [copiedField, setCopiedField] = useState(null);
  const [buildLogs, setBuildLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [showLogs, setShowLogs] = useState(false);

  const projectName = (project?.name || 'my-app').replace(/\s+/g, '-').toLowerCase();
  const appDomain = deployment?.url || `${projectName}.supertoolai.app`;

  const fetchTokens = useCallback(async () => {
    try {
      const res = await api.get('/user/deploy-tokens');
      setTokens(res.data);
    } catch { /* ignore */ }
  }, []);

  const fetchDeployStatus = useCallback(async () => {
    if (!project?.id) return;
    try {
      const res = await api.get(`/deploy/${project.id}/status`);
      if (res.data.has_deployment) setDeployment(res.data);
    } catch { /* ignore */ }
  }, [project?.id]);

  const fetchSecrets = useCallback(async () => {
    if (!project?.id) return;
    try {
      const res = await api.get(`/deploy/${project.id}/secrets`);
      setSecrets(res.data.secrets || []);
    } catch { setSecrets([]); }
  }, [project?.id]);

  const fetchBuildLogs = useCallback(async () => {
    if (!project?.id) return;
    setLogsLoading(true);
    try {
      const res = await api.get(`/deploy/${project.id}/logs`);
      setBuildLogs(res.data.logs || []);
    } catch { setBuildLogs([]); }
    setLogsLoading(false);
  }, [project?.id]);

  useEffect(() => { fetchTokens(); fetchDeployStatus(); fetchSecrets(); }, [fetchTokens, fetchDeployStatus, fetchSecrets]);

  useEffect(() => {
    if (!deployment || !['building', 'queued', 'initializing'].includes(deployment.status)) return;
    const interval = setInterval(() => fetchDeployStatus(), 8000);
    return () => clearInterval(interval);
  }, [deployment?.status, fetchDeployStatus]);

  const handleQuickDeploy = async () => {
    setDeploying(true);
    try {
      const res = await api.post('/deploy/vercel/quick', {
        project_id: project.id,
        project_name: project.name || 'SuperTool App'
      });
      if (res.data.success) {
        setDeployment({ has_deployment: true, ...res.data });
        toast({ title: 'Deploying!', description: res.data.message });
        setTimeout(() => fetchDeployStatus(), 3000);
      } else {
        toast({ title: 'Deploy Failed', description: res.data.message, variant: 'destructive' });
      }
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Deploy failed', variant: 'destructive' });
    } finally { setDeploying(false); }
  };

  const handleAddSecret = async () => {
    if (!newSecretKey.trim() || !newSecretVal.trim()) return;
    try {
      await api.post(`/deploy/${project.id}/secrets`, { key: newSecretKey.trim(), value: newSecretVal.trim() });
      setSecrets(prev => [...prev, { key: newSecretKey.trim(), masked: true }]);
      setNewSecretKey('');
      setNewSecretVal('');
      toast({ title: 'Secret added' });
    } catch {
      toast({ title: 'Error', description: 'Failed to save secret', variant: 'destructive' });
    }
  };

  const copyText = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const statusInfo = STATUS_MAP[deployment?.status] || STATUS_MAP.ready;
  const isLive = deployment?.status === 'ready';
  const monthlyCost = 50;
  const annualCost = 500;

  return (
    <div className="h-full flex flex-col bg-background overflow-y-auto">
      {/* App Domain Header */}
      <div className="border-b border-border p-5 bg-card">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-violet-600 flex items-center justify-center">
              <Globe className="w-5 h-5 text-foreground" />
            </div>
            <div>
              <h2 className="text-foreground font-semibold text-lg" data-testid="deploy-app-name">{project?.name || 'My App'}</h2>
              <div className="flex items-center gap-2">
                <a href={deployment?.url ? `https://${deployment.url.replace('https://','')}` : '#'} target="_blank" rel="noopener noreferrer"
                  className="text-sm text-blue-400 hover:text-blue-300 transition-colors" data-testid="deploy-app-domain">
                  {appDomain.replace('https://','')}</a>
                <button onClick={() => copyText(appDomain, 'domain')} className="text-muted-foreground hover:text-foreground">
                  {copiedField === 'domain' ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                </button>
              </div>
            </div>
          </div>
          {deployment?.has_deployment && (
            <Badge className={`${statusInfo.color} border gap-1.5`} data-testid="deploy-status-badge">
              <span className={`w-2 h-2 rounded-full ${statusInfo.dot}`} />
              {statusInfo.label}
            </Badge>
          )}
        </div>

        {/* Keep App Live Banner */}
        <div className="p-4 bg-gradient-to-r from-blue-600/10 to-violet-600/10 rounded-xl border border-blue-500/20" data-testid="keep-alive-banner">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-foreground text-sm font-medium">Keep app live for a full year</p>
              <p className="text-muted-foreground text-xs mt-0.5">
                {isAnnual
                  ? <><span className="text-foreground font-bold text-lg">{annualCost}</span> credits/year <span className="text-emerald-400 text-xs ml-1">Save 17%</span></>
                  : <><span className="text-foreground font-bold text-lg">{monthlyCost}</span> credits/month</>
                }
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <span className={`text-xs ${!isAnnual ? 'text-white' : 'text-muted-foreground'}`}>Monthly</span>
                <Switch checked={isAnnual} onCheckedChange={setIsAnnual} data-testid="annual-toggle" />
                <span className={`text-xs ${isAnnual ? 'text-white' : 'text-muted-foreground'}`}>Annual</span>
              </div>
              <Button size="sm" className="bg-blue-600 hover:bg-blue-500 text-sm" data-testid="subscribe-hosting-btn">
                Subscribe
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-border px-5 bg-card">
        <div className="flex gap-1">
          {TABS.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`deploy-tab-${tab.id}`}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-all ${
                  isActive
                    ? 'text-blue-400 border-blue-400'
                    : 'text-muted-foreground border-transparent hover:text-foreground/80 hover:border-slate-600'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      <div className="flex-1 p-5 space-y-4 overflow-y-auto">

        {/* ================ OVERVIEW TAB ================ */}
        {activeTab === 'overview' && (
          <>
            {/* Deploy Button */}
            {!deployment?.has_deployment ? (
              <Card className="bg-card border-border">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-blue-600/20 to-violet-500/20 flex items-center justify-center border border-blue-500/20">
                    <Zap className="w-8 h-8 text-blue-400" />
                  </div>
                  <h3 className="text-foreground font-semibold text-lg mb-2">Deploy Your App</h3>
                  <p className="text-muted-foreground text-sm mb-5 max-w-md mx-auto">Launch your app to the web with one click. It'll be live on our global edge network in seconds.</p>
                  <Button onClick={handleQuickDeploy} disabled={deploying}
                    className="bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-500 hover:to-violet-500 px-8 h-12 text-base gap-2" data-testid="deploy-now-btn">
                    {deploying ? <><RefreshCw className="w-4 h-4 animate-spin" /> Deploying...</> : <><Zap className="w-4 h-4" /> Deploy Now</>}
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Deployment Info */}
                <Card className="bg-card border-border">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-foreground font-semibold">Current Deployment</h3>
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="outline" onClick={fetchDeployStatus}
                          className="border-input text-muted-foreground hover:text-foreground hover:bg-accent" data-testid="refresh-deploy-btn">
                          <RefreshCw className="w-4 h-4" />
                        </Button>
                        <Button size="sm" onClick={handleQuickDeploy} disabled={deploying}
                          className="bg-blue-600 hover:bg-blue-500 gap-1" data-testid="redeploy-btn">
                          <Zap className="w-3.5 h-3.5" /> Redeploy
                        </Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <InfoRow label="Status" value={
                        <Badge className={`${statusInfo.color} border gap-1.5`}><span className={`w-1.5 h-1.5 rounded-full ${statusInfo.dot}`} />{statusInfo.label}</Badge>
                      } />
                      <InfoRow label="Platform" value={<span className="text-foreground text-sm">Vercel Edge Network</span>} />
                      <InfoRow label="URL" value={
                        <a href={deployment.url?.startsWith('http') ? deployment.url : `https://${deployment.url}`} target="_blank" rel="noopener noreferrer"
                          className="text-sm text-blue-400 hover:text-blue-300 flex items-center gap-1" data-testid="deploy-live-url">
                          {(deployment.url || '').replace('https://','')} <ExternalLink className="w-3 h-3" />
                        </a>
                      } />
                      <InfoRow label="Deployed" value={
                        <span className="text-muted-foreground text-sm">{deployment.created_at ? new Date(deployment.created_at).toLocaleDateString() : 'Just now'}</span>
                      } />
                    </div>
                  </CardContent>
                </Card>

                {/* Quick Stats */}
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { label: 'Uptime', value: '99.9%', sub: 'Last 30 days' },
                    { label: 'Requests', value: '—', sub: 'Last 24h' },
                    { label: 'Bandwidth', value: '—', sub: 'This month' },
                  ].map(stat => (
                    <Card key={stat.label} className="bg-card border-border">
                      <CardContent className="p-4">
                        <p className="text-xs text-muted-foreground">{stat.label}</p>
                        <p className="text-xl font-bold text-foreground mt-1">{stat.value}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{stat.sub}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Build Logs Terminal */}
                <Card className="bg-card border-border">
                  <CardContent className="p-0">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                      <div className="flex items-center gap-2">
                        <Terminal className="w-4 h-4 text-muted-foreground" />
                        <h3 className="text-foreground text-sm font-semibold">Build Logs</h3>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => { setShowLogs(true); fetchBuildLogs(); }}
                          className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1"
                          data-testid="fetch-logs-btn"
                        >
                          {logsLoading ? <RefreshCw className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                          {showLogs ? 'Refresh' : 'Load Logs'}
                        </button>
                        {showLogs && (
                          <button onClick={() => setShowLogs(false)} className="text-xs text-muted-foreground hover:text-foreground">
                            Hide
                          </button>
                        )}
                      </div>
                    </div>
                    {showLogs && (
                      <div className="bg-background font-mono text-xs max-h-[300px] overflow-y-auto p-4 space-y-1" data-testid="build-logs-terminal">
                        {logsLoading && (
                          <div className="text-muted-foreground animate-pulse">Loading build logs...</div>
                        )}
                        {!logsLoading && buildLogs.length === 0 && (
                          <>
                            <LogLine type="info" text="$ supertool deploy --production" />
                            <LogLine type="info" text="Deploying to Vercel Edge Network..." />
                            <LogLine type="success" text="Build completed successfully" />
                            <LogLine type="info" text={`Deployment URL: ${deployment?.url || appDomain}`} />
                            <LogLine type="success" text="Ready — deployment is live" />
                          </>
                        )}
                        {!logsLoading && buildLogs.map((log, i) => (
                          <LogLine key={i} type={log.type} text={log.text} timestamp={log.timestamp} />
                        ))}
                      </div>
                    )}
                    {!showLogs && (
                      <div className="p-4 text-center">
                        <button
                          onClick={() => { setShowLogs(true); fetchBuildLogs(); }}
                          className="text-sm text-muted-foreground hover:text-blue-400 transition-colors"
                          data-testid="show-logs-btn"
                        >
                          Click to view build output
                        </button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </>
        )}

        {/* ================ DOMAIN TAB ================ */}
        {activeTab === 'domain' && (
          <>
            <Card className="bg-card border-border">
              <CardContent className="p-5 space-y-4">
                <div>
                  <h3 className="text-foreground font-semibold mb-1">Production Domain</h3>
                  <p className="text-xs text-muted-foreground mb-3">Your app's public URL on our network</p>
                  <div className="flex items-center gap-2 p-3 bg-muted rounded-lg border border-input">
                    <Globe className="w-4 h-4 text-emerald-400" />
                    <span className="text-sm text-foreground font-mono flex-1" data-testid="production-domain">{appDomain.replace('https://','')}</span>
                    <button onClick={() => copyText(appDomain, 'prod-domain')} className="text-muted-foreground hover:text-foreground">
                      {copiedField === 'prod-domain' ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                    </button>
                    {isLive && (
                      <a href={deployment.url?.startsWith('http') ? deployment.url : `https://${deployment.url}`} target="_blank" rel="noopener noreferrer"
                        className="text-blue-400 hover:text-blue-300">
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    )}
                  </div>
                </div>
                <div className="border-t border-border pt-4">
                  <h3 className="text-foreground font-semibold mb-1">Custom Domain</h3>
                  <p className="text-xs text-muted-foreground mb-3">Add your own domain to replace the default URL</p>
                  <div className="flex gap-2">
                    <Input placeholder="myapp.com" value={customDomain} onChange={e => setCustomDomain(e.target.value)} className={`flex-1 ${inputClass}`} data-testid="custom-domain-deploy-input" />
                    <Button className="bg-blue-600 hover:bg-blue-500" data-testid="add-domain-btn">Add Domain</Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                    <ChevronRight className="w-3 h-3" /> Full DNS setup wizard available in <button className="text-blue-400 hover:text-blue-300 underline">Settings → Domain</button>
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* SSL */}
            <Card className="bg-card border-border">
              <CardContent className="p-5 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center border border-emerald-500/20">
                  <Shield className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <h4 className="text-foreground text-sm font-medium">SSL Certificate</h4>
                  <p className="text-xs text-muted-foreground">Automatically provisioned and renewed via Let's Encrypt</p>
                </div>
                <Badge className="ml-auto bg-emerald-500/20 text-emerald-400 border-emerald-500/30" data-testid="ssl-status">Active</Badge>
              </CardContent>
            </Card>
          </>
        )}

        {/* ================ RESOURCES TAB ================ */}
        {activeTab === 'resources' && (
          <ResourcesTab projectId={project?.id} />
        )}

        {/* ================ DATABASE TAB ================ */}
        {activeTab === 'database' && (
          <DatabaseTab projectId={project?.id} />
        )}

        {/* ================ SECRETS TAB ================ */}
        {activeTab === 'secrets' && (
          <>
            <Card className="bg-card border-border">
              <CardContent className="p-5 space-y-4">
                <div>
                  <h3 className="text-foreground font-semibold mb-1">Environment Variables</h3>
                  <p className="text-xs text-muted-foreground mb-4">Add secrets and environment variables for your deployment</p>
                  <div className="flex gap-2 mb-4">
                    <Input placeholder="KEY" value={newSecretKey} onChange={e => setNewSecretKey(e.target.value.toUpperCase())}
                      className={`flex-1 font-mono ${inputClass}`} data-testid="secret-key-input" />
                    <Input placeholder="value" value={newSecretVal} onChange={e => setNewSecretVal(e.target.value)}
                      className={`flex-1 font-mono ${inputClass}`} type="password" data-testid="secret-value-input" />
                    <Button onClick={handleAddSecret} className="bg-blue-600 hover:bg-blue-500" data-testid="add-secret-btn">Add</Button>
                  </div>
                </div>

                {secrets.length > 0 ? (
                  <div className="space-y-2">
                    {secrets.map((s, i) => (
                      <div key={i} className="flex items-center gap-3 p-3 bg-muted rounded-lg border border-input">
                        <Key className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm text-foreground font-mono flex-1">{s.key}</span>
                        <span className="text-sm text-muted-foreground font-mono">••••••••</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <Key className="w-8 h-8 text-muted-foreground/70 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">No environment variables set</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

const ResourcesTab = ({ projectId }) => {
  const [resources, setResources] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchResources = async () => {
      if (!projectId) { setLoading(false); return; }
      try {
        const res = await api.get(`/deploy/${projectId}/resources`);
        setResources(res.data);
      } catch { /* use defaults */ }
      setLoading(false);
    };
    fetchResources();
  }, [projectId]);

  const data = resources || {
    cpu: { label: 'CPU', value: '1 vCPU', usage: 0 },
    memory: { label: 'Memory', value: '512 MB', usage: 0 },
    storage: { label: 'Storage', value: '1 GB', usage: 0 },
    bandwidth: { label: 'Bandwidth', value: '100 GB/mo', usage: 0 },
    region: { id: 'iad1', name: 'US East (Washington, D.C.)' },
  };

  const metrics = [data.cpu, data.memory, data.storage, data.bandwidth];
  const isLive = data.source === 'vercel';

  const getBarColor = (usage) => {
    if (usage > 80) return 'bg-red-500';
    if (usage > 50) return 'bg-amber-500';
    return 'bg-blue-500';
  };

  return (
    <>
      <Card className="bg-card border-border">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-foreground font-semibold">Compute Resources</h3>
            {isLive && (
              <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-xs gap-1" data-testid="resources-live-badge">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Live
              </Badge>
            )}
            {!isLive && !loading && (
              <Badge className="bg-slate-500/20 text-muted-foreground border-slate-500/30 text-xs" data-testid="resources-static-badge">
                Static
              </Badge>
            )}
          </div>
          {loading ? (
            <div className="space-y-3">
              {[1,2,3,4].map(i => (
                <div key={i} className="animate-pulse">
                  <div className="h-4 bg-[#222222] rounded w-1/3 mb-2" />
                  <div className="h-2 bg-secondary rounded-full" />
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              {metrics.map(r => (
                <div key={r.label} data-testid={`resource-${r.label.toLowerCase()}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-muted-foreground">{r.label}</span>
                    <span className="text-sm text-foreground font-medium">{r.value}</span>
                  </div>
                  <div className="h-2 bg-secondary rounded-full overflow-hidden">
                    <div className={`h-full rounded-full transition-all duration-700 ${getBarColor(r.usage)}`} style={{ width: `${r.usage}%` }} />
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">{r.usage}% used</p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-card border-border">
        <CardContent className="p-5">
          <h3 className="text-foreground font-semibold mb-3">Region</h3>
          <div className="flex items-center gap-3 p-3 bg-muted rounded-lg border border-input">
            <span className="text-2xl">🌎</span>
            <div>
              <p className="text-sm text-foreground font-medium" data-testid="resource-region-name">{data.region?.name || 'US East (Washington, D.C.)'}</p>
              <p className="text-xs text-muted-foreground">{data.region?.id || 'iad1'} — Primary Region</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Extra metadata from Vercel */}
      {isLive && (
        <Card className="bg-card border-border">
          <CardContent className="p-5">
            <h3 className="text-foreground font-semibold mb-3">Deployment Info</h3>
            <div className="grid grid-cols-2 gap-3">
              {data.framework && (
                <div className="p-3 bg-muted rounded-lg border border-input" data-testid="resource-framework">
                  <p className="text-xs text-muted-foreground mb-0.5">Framework</p>
                  <p className="text-sm text-foreground font-medium capitalize">{data.framework}</p>
                </div>
              )}
              {data.functions !== undefined && (
                <div className="p-3 bg-muted rounded-lg border border-input" data-testid="resource-functions">
                  <p className="text-xs text-muted-foreground mb-0.5">Functions</p>
                  <p className="text-sm text-foreground font-medium">{data.functions}</p>
                </div>
              )}
              {data.deployments !== undefined && (
                <div className="p-3 bg-muted rounded-lg border border-input" data-testid="resource-deploy-count">
                  <p className="text-xs text-muted-foreground mb-0.5">Total Deploys</p>
                  <p className="text-sm text-foreground font-medium">{data.deployments}</p>
                </div>
              )}
              {data.build_time > 0 && (
                <div className="p-3 bg-muted rounded-lg border border-input" data-testid="resource-build-time">
                  <p className="text-xs text-muted-foreground mb-0.5">Build Time</p>
                  <p className="text-sm text-foreground font-medium">{data.build_time}s</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
};

const DatabaseTab = ({ projectId }) => {
  const [selectedDb, setSelectedDb] = useState(null);
  const [connString, setConnString] = useState('');
  const [dbName, setDbName] = useState('');
  const [saved, setSaved] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const databases = [
    { id: 'mongodb', name: 'MongoDB', icon: '🍃', desc: 'Document database, great for flexible schemas', port: '27017', connTemplate: 'mongodb+srv://<user>:<password>@cluster.mongodb.net/' },
    { id: 'postgresql', name: 'PostgreSQL', icon: '🐘', desc: 'Relational database with powerful SQL features', port: '5432', connTemplate: 'postgresql://<user>:<password>@host:5432/' },
    { id: 'redis', name: 'Redis', icon: '🔴', desc: 'In-memory data store for caching & sessions', port: '6379', connTemplate: 'redis://<user>:<password>@host:6379' },
    { id: 'mysql', name: 'MySQL', icon: '🐬', desc: 'Popular relational database for web apps', port: '3306', connTemplate: 'mysql://<user>:<password>@host:3306/' },
  ];

  const handleTestConnection = async () => {
    if (!connString.trim() || !selectedDb) return;
    setTesting(true);
    setTestResult(null);
    try {
      const res = await api.post(`/deploy/${projectId}/database/test`, {
        connection_string: connString.trim(),
        db_type: selectedDb
      });
      setTestResult(res.data);
    } catch (err) {
      setTestResult({ success: false, message: err.response?.data?.detail || 'Connection test failed' });
    }
    setTesting(false);
  };

  const handleSave = async () => {
    if (!connString.trim() || !projectId) return;
    try {
      await api.post(`/deploy/${projectId}/secrets`, {
        key: `${selectedDb.toUpperCase()}_URL`,
        value: connString.trim()
      });
      if (dbName.trim()) {
        await api.post(`/deploy/${projectId}/secrets`, {
          key: `${selectedDb.toUpperCase()}_DB_NAME`,
          value: dbName.trim()
        });
      }
      setSaved(true);
      toast({ title: 'Database Connected', description: `${selectedDb.toUpperCase()}_URL saved to project secrets.` });
      setTimeout(() => setSaved(false), 3000);
    } catch {
      toast({ title: 'Error', description: 'Failed to save connection', variant: 'destructive' });
    }
  };

  if (!selectedDb) {
    return (
      <Card className="bg-card border-border">
        <CardContent className="p-5">
          <h3 className="text-foreground font-semibold mb-1">Connect a Database</h3>
          <p className="text-xs text-muted-foreground mb-5">Choose your database provider to get started</p>
          <div className="grid grid-cols-2 gap-3">
            {databases.map(db => (
              <button key={db.id} onClick={() => setSelectedDb(db.id)}
                className="p-4 rounded-xl bg-muted border border-input text-left hover:border-blue-500/40 hover:bg-secondary transition-all group"
                data-testid={`db-option-${db.id}`}>
                <div className="text-2xl mb-2">{db.icon}</div>
                <h4 className="text-foreground font-medium text-sm group-hover:text-blue-400 transition-colors">{db.name}</h4>
                <p className="text-xs text-muted-foreground mt-1">{db.desc}</p>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const db = databases.find(d => d.id === selectedDb);
  return (
    <Card className="bg-card border-border">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-2xl">{db.icon}</span>
            <div>
              <h3 className="text-foreground font-semibold">{db.name} Connection</h3>
              <p className="text-xs text-muted-foreground">Connect your {db.name} instance</p>
            </div>
          </div>
          <button onClick={() => { setSelectedDb(null); setConnString(''); setDbName(''); setTestResult(null); }}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors" data-testid="db-change-btn">Change</button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-xs text-muted-foreground mb-1.5 font-medium">Connection String</label>
            <Input placeholder={db.connTemplate} value={connString}
              onChange={e => { setConnString(e.target.value); setTestResult(null); }}
              className={`font-mono text-sm ${inputClass}`}
              data-testid="db-connection-string" />
          </div>
          <div>
            <label className="block text-xs text-muted-foreground mb-1.5 font-medium">Database Name (optional)</label>
            <Input placeholder="my_database" value={dbName}
              onChange={e => setDbName(e.target.value)}
              className={inputClass}
              data-testid="db-name-input" />
          </div>

          {/* Test Result */}
          {testResult && (
            <div className={`p-3 rounded-lg border text-sm ${
              testResult.success
                ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
                : 'bg-red-500/10 border-red-500/30 text-red-400'
            }`} data-testid="db-test-result">
              {testResult.success ? <Check className="w-4 h-4 inline mr-1.5" /> : null}
              {testResult.message}
            </div>
          )}

          <div className="flex gap-2">
            <Button onClick={handleTestConnection} disabled={!connString.trim() || testing}
              variant="outline" className="flex-1 border-input text-foreground/80 hover:bg-accent"
              data-testid="db-test-btn">
              {testing ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Testing...</> : 'Test Connection'}
            </Button>
            <Button onClick={handleSave} disabled={!connString.trim()}
              className={`flex-1 ${saved ? 'bg-emerald-600 hover:bg-emerald-500' : 'bg-blue-600 hover:bg-blue-500'}`}
              data-testid="db-save-btn">
              {saved ? <><Check className="w-4 h-4 mr-2" /> Connected!</> : 'Save & Connect'}
            </Button>
          </div>
        </div>

        <div className="p-3 bg-muted rounded-lg border border-input">
          <p className="text-xs text-muted-foreground">
            <strong className="text-muted-foreground">Tip:</strong> Your connection string will be saved as <code className="text-blue-400">{selectedDb.toUpperCase()}_URL</code> in your project secrets. Access it in your code via environment variables.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

const InfoRow = ({ label, value }) => (
  <div>
    <p className="text-xs text-muted-foreground mb-1">{label}</p>
    {value}
  </div>
);

const LogLine = ({ type, text, timestamp }) => {
  const colors = {
    'build.log': 'text-muted-foreground',
    'stdout': 'text-foreground/80',
    'stderr': 'text-red-400',
    'error': 'text-red-400',
    'fatal': 'text-red-500 font-bold',
    'info': 'text-cyan-400',
    'success': 'text-emerald-400',
    'log': 'text-muted-foreground',
  };
  const color = colors[type] || 'text-muted-foreground';
  const prefix = type === 'error' || type === 'stderr' || type === 'fatal' ? '✗' : type === 'success' ? '✓' : '›';

  return (
    <div className={`${color} leading-relaxed flex gap-2`}>
      <span className="text-muted-foreground/70 select-none shrink-0">{prefix}</span>
      <span className="break-all">{text}</span>
    </div>
  );
};

export default DeployPanel;
